<template>
  <section 
    id="what-i-offer" 
    ref="offerSection" 
    class="w-full relative py-20 overflow-hidden font-poppins"
    style="position: relative; background-color: #FAF9F7;"
  >
    <!-- Solid background to ensure content is visible -->
    <div class="absolute inset-0 bg-[#FAF9F7]"></div>
    
    <!-- Improved background with subtle texture -->
    <div class="absolute inset-0 opacity-5" style="background-image: url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23D2919A\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v6h6v-6h-6zm6 6v6h-6v-6h6zm-6-6v-6h6v6h-6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')"></div>
    
    <!-- Smooth transitions between sections -->
    <div class="absolute top-0 left-0 w-full h-16 bg-gradient-to-b from-[#FAF9F7] to-transparent"></div>
    <div class="absolute bottom-0 left-0 w-full h-16 bg-gradient-to-t from-[#FAF9F7] to-transparent"></div>
    
    <!-- Main content area with proper z-index -->
    <div class="container mx-auto px-4 md:px-8 lg:px-16 max-w-[1600px] relative z-10">
      <!-- Section Header with refined styling -->
      <div class="mb-16 text-center">
        <h2 class="text-4xl md:text-5xl font-bold text-[#333333] mb-4">What I Offer</h2>
        <div class="h-1 w-24 mx-auto bg-gradient-to-r from-[#D2919A] to-[#D2919A]/70 rounded-full"></div>
        <p class="text-center text-[#333333] mt-6 text-lg max-w-3xl mx-auto">
          A confidential space, a relationship without judgment. Another human being who will take the time to understand, to see the world from your perspective, and to walk in your shoes.
        </p>
      </div>
      
      <!-- Frameworks, Approaches Container with improved grid layout -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-16">
        <!-- My Approach Section - Improved for better readability -->
        <div class="relative bg-white/60 p-8 rounded-xl border border-[#D2919A]/10 shadow-sm">
          <!-- Visual accent elements -->
          <div class="absolute top-0 right-0 w-40 h-40 bg-[#D2919A]/5 rounded-full blur-3xl -z-10"></div>
          
          <!-- Section header with gradient underline -->
          <div class="mb-10 relative">
            <h3 class="text-2xl font-bold text-[#333333] inline-block relative">
              My Approach
            </h3>
            <div class="h-1 w-24 mt-2 bg-gradient-to-r from-[#D2919A] to-[#D2919A]/70 rounded-full"></div>
          </div>
          
          <!-- Approach Items - Redesigned with clean, unboxed layout and better readability -->
          <div class="space-y-8">
            <!-- Client-Led Process -->
            <div class="approach-item group">
              <div class="flex items-start gap-5">
                <div class="approach-icon-minimal flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#D2919A] to-[#D2919A]/90 flex items-center justify-center shadow-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h4 class="text-xl font-semibold text-[#333333] mb-2 group-hover:text-[#D2919A] transition-colors duration-300">
                    Client-Led Process
                  </h4>
                  <p class="text-[#333333] leading-relaxed">
                    I believe in respecting your individual needs and pace. You set the direction, and I'll follow, offering guidance where helpful, but never pushing you before you're ready.
                  </p>
                  <div class="h-px w-full bg-gradient-to-r from-[#D2919A]/20 to-transparent mt-4 opacity-70"></div>
                </div>
              </div>
            </div>
            
            <!-- Judgment-Free Space -->
            <div class="approach-item group">
              <div class="flex items-start gap-5">
                <div class="approach-icon-minimal flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#D2919A] to-[#D2919A]/90 flex items-center justify-center shadow-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h4 class="text-xl font-semibold text-[#333333] mb-2 group-hover:text-[#D2919A] transition-colors duration-300">
                    Judgment-Free Space
                  </h4>
                  <p class="text-[#333333] leading-relaxed">
                    Every emotion, thought, and experience you share is valid and worthy of attention. I provide an accepting environment without judgment or criticism.
                  </p>
                  <div class="h-px w-full bg-gradient-to-r from-[#D2919A]/20 to-transparent mt-4 opacity-70"></div>
                </div>
              </div>
            </div>
            
            <!-- Relational Focus -->
            <div class="approach-item group">
              <div class="flex items-start gap-5">
                <div class="approach-icon-minimal flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#D2919A] to-[#D2919A]/90 flex items-center justify-center shadow-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <div>
                  <h4 class="text-xl font-semibold text-[#333333] mb-2 group-hover:text-[#D2919A] transition-colors duration-300">
                    Relational Focus
                  </h4>
                  <p class="text-[#333333] leading-relaxed">
                    I value the therapeutic relationship we build together. This safe, supportive connection becomes the foundation for exploring difficult experiences and fostering growth.
                  </p>
                  <div class="h-px w-full bg-gradient-to-r from-[#D2919A]/20 to-transparent mt-4 opacity-70"></div>
                </div>
              </div>
            </div>
            
            <!-- Adaptable Techniques -->
            <div class="approach-item group">
              <div class="flex items-start gap-5">
                <div class="approach-icon-minimal flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#D2919A] to-[#D2919A]/90 flex items-center justify-center shadow-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
                  </svg>
                </div>
                <div>
                  <h4 class="text-xl font-semibold text-[#333333] mb-2 group-hover:text-[#D2919A] transition-colors duration-300">
                    Adaptable Techniques
                  </h4>
                  <p class="text-[#333333] leading-relaxed">
                    I integrate various counselling approaches based on what works best for you. Your needs are unique, and our work together will reflect that.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Frameworks Section - Enhanced with better mobile responsive design -->
        <div class="relative bg-white/60 p-8 rounded-xl border border-[#D2919A]/10 shadow-sm">
          <!-- Visual accent elements -->
          <div class="absolute bottom-0 left-0 w-40 h-40 bg-[#D2919A]/5 rounded-full blur-3xl -z-10"></div>
          
          <!-- Section header with gradient underline -->
          <div class="mb-10 relative">
            <h3 class="text-2xl font-bold text-[#333333] inline-block relative">
              Therapeutic Frameworks I Work With
            </h3>
            <div class="h-1 w-24 mt-2 bg-gradient-to-r from-[#D2919A] to-[#D2919A]/70 rounded-full"></div>
          </div>
          
          <!-- Framework Items - Mobile-optimized collapsible layout -->
          <div class="space-y-6">
            <!-- Person-Centred Therapy - Collapsed by default on mobile -->
            <div class="framework-collapsible">
              <div class="framework-header flex items-center justify-between p-4 bg-[#FAF9F7]/50 rounded-lg cursor-pointer border border-[#D2919A]/10 md:cursor-default md:border-0 md:p-0 md:bg-transparent" @click="toggleFramework('personCentred')">
                <div class="flex items-center gap-4">
                  <div class="framework-icon-minimal flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#D2919A] to-[#D2919A]/90 flex items-center justify-center shadow-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <h4 class="text-xl font-semibold text-[#333333]">Person-Centred Therapy</h4>
                </div>
                <!-- Mobile only toggle icon -->
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-[#D2919A] md:hidden" :class="{'rotate-180': expandedFrameworks.personCentred}" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              <!-- Content - Always visible on desktop, collapsible on mobile -->
              <div class="framework-content pl-14 pr-4 mt-2 md:mt-3" :class="{'hidden md:block': !expandedFrameworks.personCentred}">
                <p class="text-[#333333] leading-relaxed">
                  A humanistic approach focused on creating conditions that enhance natural growth and development to facilitate self-actualisation.
                </p>
                <div class="h-px w-full bg-gradient-to-r from-[#D2919A]/20 to-transparent mt-4 mb-6 opacity-70 md:block"></div>
              </div>
            </div>
            
            <!-- Psychodynamic Therapy -->
            <div class="framework-collapsible">
              <div class="framework-header flex items-center justify-between p-4 bg-[#FAF9F7]/50 rounded-lg cursor-pointer border border-[#D2919A]/10 md:cursor-default md:border-0 md:p-0 md:bg-transparent" @click="toggleFramework('psychodynamic')">
                <div class="flex items-center gap-4">
                  <div class="framework-icon-minimal flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#D2919A] to-[#D2919A]/90 flex items-center justify-center shadow-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <h4 class="text-xl font-semibold text-[#333333]">Psychodynamic Therapy</h4>
                </div>
                <!-- Mobile only toggle icon -->
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-[#D2919A] md:hidden" :class="{'rotate-180': expandedFrameworks.psychodynamic}" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              <!-- Content - Always visible on desktop, collapsible on mobile -->
              <div class="framework-content pl-14 pr-4 mt-2 md:mt-3" :class="{'hidden md:block': !expandedFrameworks.psychodynamic}">
                <p class="text-[#333333] leading-relaxed">
                  Rooted in the belief that childhood experiences and unconscious thoughts, feelings, urges, and desires influence our current behavior.
                </p>
                <div class="h-px w-full bg-gradient-to-r from-[#D2919A]/20 to-transparent mt-4 mb-6 opacity-70 md:block"></div>
              </div>
            </div>
            
            <!-- CBT -->
            <div class="framework-collapsible">
              <div class="framework-header flex items-center justify-between p-4 bg-[#FAF9F7]/50 rounded-lg cursor-pointer border border-[#D2919A]/10 md:cursor-default md:border-0 md:p-0 md:bg-transparent" @click="toggleFramework('cbt')">
                <div class="flex items-center gap-4">
                  <div class="framework-icon-minimal flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#D2919A] to-[#D2919A]/90 flex items-center justify-center shadow-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h4 class="text-xl font-semibold text-[#333333]">CBT (Cognitive Behavioural Therapy)</h4>
                </div>
                <!-- Mobile only toggle icon -->
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-[#D2919A] md:hidden" :class="{'rotate-180': expandedFrameworks.cbt}" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              <!-- Content - Always visible on desktop, collapsible on mobile -->
              <div class="framework-content pl-14 pr-4 mt-2 md:mt-3" :class="{'hidden md:block': !expandedFrameworks.cbt}">
                <p class="text-[#333333] leading-relaxed">
                  A goal-oriented therapy that takes a practical approach to problem-solving, with the aim of changing patterns of thinking or behavior.
                </p>
                <div class="h-px w-full bg-gradient-to-r from-[#D2919A]/20 to-transparent mt-4 mb-6 opacity-70 md:block"></div>
              </div>
            </div>
            
            <!-- Existential Therapy -->
            <div class="framework-collapsible">
              <div class="framework-header flex items-center justify-between p-4 bg-[#FAF9F7]/50 rounded-lg cursor-pointer border border-[#D2919A]/10 md:cursor-default md:border-0 md:p-0 md:bg-transparent" @click="toggleFramework('existential')">
                <div class="flex items-center gap-4">
                  <div class="framework-icon-minimal flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#D2919A] to-[#D2919A]/90 flex items-center justify-center shadow-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h4 class="text-xl font-semibold text-[#333333]">Existential Therapy</h4>
                </div>
                <!-- Mobile only toggle icon -->
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-[#D2919A] md:hidden" :class="{'rotate-180': expandedFrameworks.existential}" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              <!-- Content - Always visible on desktop, collapsible on mobile -->
              <div class="framework-content pl-14 pr-4 mt-2 md:mt-3" :class="{'hidden md:block': !expandedFrameworks.existential}">
                <p class="text-[#333333] leading-relaxed">
                  Explores how to find meaning and purpose in life, addressing issues such as freedom, responsibility, and the search for authentic existence.
                </p>
                <div class="h-px w-full bg-gradient-to-r from-[#D2919A]/20 to-transparent mt-4 mb-6 opacity-70 md:block"></div>
              </div>
            </div>
            
            <!-- Gestalt Therapy -->
            <div class="framework-collapsible">
              <div class="framework-header flex items-center justify-between p-4 bg-[#FAF9F7]/50 rounded-lg cursor-pointer border border-[#D2919A]/10 md:cursor-default md:border-0 md:p-0 md:bg-transparent" @click="toggleFramework('gestalt')">
                <div class="flex items-center gap-4">
                  <div class="framework-icon-minimal flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#D2919A] to-[#D2919A]/90 flex items-center justify-center shadow-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h4 class="text-xl font-semibold text-[#333333]">Gestalt Therapy</h4>
                </div>
                <!-- Mobile only toggle icon -->
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-[#D2919A] md:hidden" :class="{'rotate-180': expandedFrameworks.gestalt}" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              <!-- Content - Always visible on desktop, collapsible on mobile -->
              <div class="framework-content pl-14 pr-4 mt-2 md:mt-3" :class="{'hidden md:block': !expandedFrameworks.gestalt}">
                <p class="text-[#333333] leading-relaxed">
                  Focuses on present experiences and awareness, helping clients recognise how current challenges connect to past experiences and relationships.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Experience Areas Section -->
      <div class="mt-16 relative">
        <!-- Section header with gradient underline -->
        <div class="mb-6 relative">
          <h3 class="text-2xl font-bold text-[#333333] inline-block relative">
            Experience With
          </h3>
          <div class="h-1 w-24 mt-2 bg-[#D2919A] rounded-full"></div>
        </div>
        
        <!-- Category Navigation Tabs - simplified and consistent colors -->
        <div class="mb-6">
          <div class="flex flex-wrap justify-start gap-2 px-2">
            <button 
              @click="activeCategory = 'all'; showingMore = false"
              :class="[
                'px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300',
                activeCategory === 'all' 
                  ? 'bg-[#D2919A] text-white shadow-sm'
                  : 'bg-white text-[#333333] hover:bg-[#D2919A]/5 border border-[#D2919A]/20'
              ]"
            >
              All Areas
            </button>
            <button 
              v-for="(_, category) in categories" 
              :key="category"
              @click="activeCategory = category; showingMore = false"
              :class="[
                'px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300',
                activeCategory === category 
                  ? 'bg-[#D2919A] text-white shadow-sm'
                  : 'bg-white text-[#333333] hover:bg-[#D2919A]/5 border border-[#D2919A]/20'
              ]"
            >
              {{ formatCategory(category) }}
            </button>
          </div>
        </div>
        
        <!-- Desktop Layout - Hidden on mobile - with consistent colors -->
        <div class="hidden md:block" v-if="activeCategory === 'all'">
          <!-- Mental Health Row - 8/10 width -->
          <div class="grid grid-cols-10 gap-3 mb-4">
            <!-- Mental Health - 8/10 width -->
            <div 
              class="col-span-10 md:col-span-8 category-group p-5 bg-white rounded-xl border border-[#D2919A]/10 shadow-sm hover:shadow-md transition-all duration-300 h-auto cursor-pointer"
              @click="toggleCategory('mentalHealth')"
            >
              <h3 class="text-lg font-bold text-[#333333] mb-3 relative inline-block">
                Mental Health
                <span class="absolute -bottom-1 left-0 w-12 h-[2px] bg-[#D2919A]"></span>
              </h3>
              
              <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 relative overflow-hidden" :style="{ maxHeight: showMoreDesktop || expandedCategories.mentalHealth ? 'none' : '150px' }">
                <div 
                  v-for="(item, index) in categories.mentalHealth" 
                  :key="`mentalHealth-${index}`"
                  class="py-2 px-3 bg-[#FAF9F7] rounded-lg transition-all duration-300 flex items-center gap-2 border border-[#D2919A]/10 hover:border-[#D2919A]/20"
                >
                  <div class="h-5 w-5 rounded-full bg-[#D2919A] flex items-center justify-center flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span class="text-[#333333] font-medium">{{ item }}</span>
                </div>
                <div v-if="!showMoreDesktop && !expandedCategories.mentalHealth && categories.mentalHealth.length > 2" class="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white via-white/80 to-transparent flex justify-center items-end pb-1">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-[#D2919A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            <!-- Developmental & Cognitive - 2/10 width -->
            <div 
              class="col-span-10 md:col-span-2 category-group p-5 bg-white rounded-xl border border-[#D2919A]/10 shadow-sm hover:shadow-md transition-all duration-300 h-auto cursor-pointer"
            >
              <h3 class="text-lg font-bold text-[#333333] mb-3 relative inline-block">
                Developmental & Cognitive
                <span class="absolute -bottom-1 left-0 w-12 h-[2px] bg-[#D2919A]"></span>
              </h3>
              
              <div class="space-y-2">
                <div 
                  v-for="(item, index) in categories.developmentalCognitive" 
                  :key="`developmentalCognitive-${index}`"
                  class="py-2 px-3 bg-[#FAF9F7] rounded-lg transition-all duration-300 flex items-center gap-2 border border-[#D2919A]/10 hover:border-[#D2919A]/20"
                >
                  <div class="h-5 w-5 rounded-full bg-[#D2919A] flex items-center justify-center flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span class="text-[#333333] font-medium">{{ item }}</span>
                </div>
                
                <!-- Removing all the incorrect category items below -->
              </div>
            </div>
          </div>
          
          <!-- Other Categories Grid - 4 columns per row for remaining categories -->
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <!-- Emotional Issues -->
            <div 
              class="category-group p-5 bg-white rounded-xl border border-[#D2919A]/10 shadow-sm hover:shadow-md transition-all duration-300 h-auto cursor-pointer"
              @click="toggleCategory('emotionalIssues')"
            >
              <h3 class="text-lg font-bold text-[#333333] mb-3 relative inline-block">
                Emotional Issues
                <span class="absolute -bottom-1 left-0 w-12 h-[2px] bg-[#D2919A]"></span>
              </h3>
              
              <div class="space-y-2 relative overflow-hidden" :style="{ maxHeight: showMoreDesktop || expandedCategories.emotionalIssues ? 'none' : '150px' }">
                <div 
                  v-for="(item, index) in categories.emotionalIssues" 
                  :key="`emotionalIssues-${index}`"
                  class="py-2 px-3 bg-[#FAF9F7] rounded-lg transition-all duration-300 flex items-center gap-2 border border-[#D2919A]/10 hover:border-[#D2919A]/20"
                >
                  <div class="h-5 w-5 rounded-full bg-[#D2919A] flex items-center justify-center flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span class="text-[#333333] font-medium">{{ item }}</span>
                </div>
                <div v-if="!showMoreDesktop && !expandedCategories.emotionalIssues && categories.emotionalIssues.length > 2" class="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white via-white/80 to-transparent flex justify-center items-end pb-1">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-[#D2919A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
            
            <!-- Addictions -->
            <div 
              class="category-group p-5 bg-white rounded-xl border border-[#D2919A]/10 shadow-sm hover:shadow-md transition-all duration-300 h-auto cursor-pointer"
              @click="toggleCategory('addictions')"
            >
              <h3 class="text-lg font-bold text-[#333333] mb-3 relative inline-block">
                Addictions
                <span class="absolute -bottom-1 left-0 w-12 h-[2px] bg-[#D2919A]"></span>
              </h3>
              
              <div class="space-y-2 relative overflow-hidden" :style="{ maxHeight: showMoreDesktop || expandedCategories.addictions ? 'none' : '150px' }">
                <div 
                  v-for="(item, index) in categories.addictions" 
                  :key="`addictions-${index}`"
                  class="py-2 px-3 bg-[#FAF9F7] rounded-lg transition-all duration-300 flex items-center gap-2 border border-[#D2919A]/10 hover:border-[#D2919A]/20"
                >
                  <div class="h-5 w-5 rounded-full bg-[#D2919A] flex items-center justify-center flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span class="text-[#333333] font-medium">{{ item }}</span>
                </div>
                <div v-if="!showMoreDesktop && !expandedCategories.addictions && categories.addictions.length > 2" class="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white via-white/80 to-transparent flex justify-center items-end pb-1">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-[#D2919A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
            
            <!-- Trauma -->
            <div 
              class="category-group p-5 bg-white rounded-xl border border-[#D2919A]/10 shadow-sm hover:shadow-md transition-all duration-300 h-auto cursor-pointer"
              @click="toggleCategory('trauma')"
            >
              <h3 class="text-lg font-bold text-[#333333] mb-3 relative inline-block">
                Trauma
                <span class="absolute -bottom-1 left-0 w-12 h-[2px] bg-[#D2919A]"></span>
              </h3>
              
              <div class="space-y-2 relative overflow-hidden" :style="{ maxHeight: showMoreDesktop || expandedCategories.trauma ? 'none' : '150px' }">
                <div 
                  v-for="(item, index) in categories.trauma" 
                  :key="`trauma-${index}`"
                  class="py-2 px-3 bg-[#FAF9F7] rounded-lg transition-all duration-300 flex items-center gap-2 border border-[#D2919A]/10 hover:border-[#D2919A]/20"
                >
                  <div class="h-5 w-5 rounded-full bg-[#D2919A] flex items-center justify-center flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span class="text-[#333333] font-medium">{{ item }}</span>
                </div>
                <div v-if="!showMoreDesktop && !expandedCategories.trauma && categories.trauma.length > 2" class="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white via-white/80 to-transparent flex justify-center items-end pb-1">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-[#D2919A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            <!-- Mobile preview - this one column will have all other categories not shown explicitly -->
            <div 
              class="category-group p-5 bg-white rounded-xl border border-[#D2919A]/10 shadow-sm hover:shadow-md transition-all duration-300 h-auto cursor-pointer"
            >
              <h3 class="text-lg font-bold text-[#333333] mb-3 relative inline-block">
                Other Areas
                <span class="absolute -bottom-1 left-0 w-12 h-[2px] bg-[#D2919A]"></span>
              </h3>
              
              <!-- Just show a preview of remaining categories -->
              <div class="relative overflow-hidden" style="max-height: 150px;">
                <div class="space-y-2">
                  <div 
                    v-for="(categoryName, index) in ['relationships', 'eatingDisorders', 'lifeChallenges']" 
                    :key="`category-preview-${index}`"
                    class="py-2 px-3 bg-[#FAF9F7] rounded-lg transition-all duration-300 flex items-center gap-2 border border-[#D2919A]/10 hover:border-[#D2919A]/20"
                  >
                    <div class="h-5 w-5 rounded-full bg-[#D2919A] flex items-center justify-center flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span class="text-[#333333] font-medium">{{ formatCategory(categoryName) }}</span>
                  </div>
                </div>
                <div class="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white via-white/80 to-transparent flex justify-center items-end pb-1">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-[#D2919A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      
        <!-- Mobile layout for all categories - ONLY shown on mobile -->
        <div class="block md:hidden mt-8">
          <!-- Mental Health preview with fade effect - ONLY when not showing more and in "all" category view -->
          <div v-if="!showingMore && activeCategory === 'all'" class="preview-container relative overflow-hidden">
            <!-- Mental Health category with limited items -->
            <div class="p-5 bg-white rounded-xl border border-[#D2919A]/10 shadow-sm">
              <h3 class="text-lg font-bold text-[#333333] mb-3 relative inline-block">
                Mental Health
                <span class="absolute -bottom-1 left-0 w-12 h-[2px] bg-[#D2919A]"></span>
              </h3>
              
              <div class="grid grid-cols-1 gap-2">
                <div 
                  v-for="(item, index) in categories.mentalHealth.slice(0, 4)" 
                  :key="`mentalHealth-preview-${index}`"
                  class="py-2 px-3 bg-[#FAF9F7] rounded-lg transition-all duration-300 flex items-center gap-2 border border-[#D2919A]/10 hover:border-[#D2919A]/20"
                >
                  <div class="h-5 w-5 rounded-full bg-[#D2919A] flex items-center justify-center flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span class="text-[#333333] font-medium">{{ item }}</span>
                </div>
              </div>
            </div>
            
            <!-- Gradient fade overlay -->
            <div class="absolute inset-0 bg-gradient-to-b from-transparent via-white/60 to-white pointer-events-none"></div>
            
            <!-- Show More button -->
            <div 
              @click="showingMore = true"
              class="absolute bottom-0 left-0 right-0 py-4 text-center cursor-pointer bg-white/80 backdrop-blur-sm border-t border-[#D2919A]/10"
            >
              <div class="relative z-10 text-[#D2919A] font-medium hover:text-[#D2919A]/80 transition-colors">
                <span class="inline-flex items-center gap-1.5">
                  Show All Experience Areas
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </div>
            </div>
          </div>
          
          <!-- Expanded all categories view when "Show More" is clicked - REPLACES the preview -->
          <div v-if="showingMore && activeCategory === 'all'" class="mt-4 space-y-6">
            <div 
              v-for="(items, categoryName) in categories" 
              :key="categoryName" 
              class="p-5 bg-white rounded-xl border border-[#D2919A]/10 shadow-sm"
            >
              <h3 class="text-lg font-bold text-[#333333] mb-3 relative inline-block capitalize">
                {{ formatCategoryName(categoryName) }}
                <span class="absolute -bottom-1 left-0 w-12 h-[2px] bg-[#D2919A]"></span>
              </h3>
              
              <div class="grid grid-cols-1 gap-2">
                <div 
                  v-for="(item, index) in items" 
                  :key="`${categoryName}-${index}`"
                  class="py-2 px-3 bg-[#FAF9F7] rounded-lg transition-all duration-300 flex items-center gap-2 border border-[#D2919A]/10 hover:border-[#D2919A]/20"
                >
                  <div class="h-5 w-5 rounded-full bg-[#D2919A] flex items-center justify-center flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span class="text-[#333333] font-medium">{{ item }}</span>
                </div>
              </div>
            </div>
            
            <!-- Show Less button -->
            <div 
              @click="showingMore = false"
              class="py-2 px-4 rounded-lg border border-[#D2919A]/20 bg-white text-center text-[#D2919A] font-medium hover:bg-[#D2919A]/5 hover:border-[#D2919A]/40 transition-all duration-300 cursor-pointer"
            >
              <span class="inline-flex items-center gap-1.5">
                Show Less
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                </svg>
              </span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Single category view -->
      <div v-if="activeCategory !== 'all'" class="mt-8">
        <div class="mb-6 flex items-center space-x-4">
          <button 
            @click="activeCategory = 'all'"
            class="inline-flex items-center gap-1.5 py-2 px-4 rounded-lg border border-[#D2919A]/20 bg-white text-[#D2919A] font-medium hover:bg-[#D2919A]/5 hover:border-[#D2919A]/40 transition-all duration-300"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to All Categories
          </button>
          
          <h3 class="text-xl font-bold text-[#333333] relative inline-block capitalize">
            {{ formatCategoryName(activeCategory) }}
            <span class="absolute -bottom-1 left-0 w-16 h-[2px] bg-[#D2919A]"></span>
          </h3>
        </div>
        
        <div class="category-group p-5 bg-white rounded-xl border border-[#D2919A]/10 shadow-sm">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div 
              v-for="(item, index) in categories[activeCategory]" 
              :key="`${activeCategory}-${index}`"
              class="py-2 px-3 bg-[#FAF9F7] rounded-lg transition-all duration-300 flex items-center gap-2 border border-[#D2919A]/10 hover:border-[#D2919A]/20"
            >
              <div class="h-5 w-5 rounded-full bg-[#D2919A] flex items-center justify-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span class="text-[#333333] font-medium">{{ item }}</span>
            </div>
          </div>
          
          <!-- Related categories section -->
          <div class="mt-8 pt-6 border-t border-[#D2919A]/10">
            <h4 class="text-lg font-semibold text-[#333333] mb-4">Related Areas of Experience</h4>
            
            <div class="flex flex-wrap gap-2">
              <button
                v-for="(_, categoryName) in otherCategories"
                :key="categoryName"
                @click="activeCategory = categoryName"
                class="py-2 px-4 rounded-lg border border-[#D2919A]/20 bg-white text-[#D2919A] font-medium hover:bg-[#D2919A]/5 hover:border-[#D2919A]/40 transition-all duration-300"
              >
                {{ formatCategoryName(categoryName) }}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Single Show More button across the entire view - only visible on desktop -->
      <div class="mt-6 text-center hidden md:block">
        <button 
          @click="toggleShowMoreDesktop"
          class="px-6 py-2 rounded-full font-medium border border-[#D2919A]/20 text-[#D2919A] hover:bg-[#D2919A]/5 hover:border-[#D2919A]/40 transition-all duration-300"
        >
          <span class="inline-flex items-center gap-2">
            {{ showMoreDesktop ? 'Show Less' : 'Show All Experience Areas' }}
            <svg v-if="!showMoreDesktop" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
            </svg>
          </span>
        </button>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'WhatIOfferSection',
  data() {
    return {
      activeCategory: 'all',
      showingMore: false,
      showMoreDesktop: false,
      // Track which individual categories are expanded
      expandedCategories: {
        mentalHealth: false,
        emotionalIssues: false,
        eatingDisorders: false,
        addictions: false,
        trauma: false,
        lifeChallenges: false,
        relationships: false,
        developmentalCognitive: false
      },
      // NEW: Track which frameworks are expanded on mobile
      expandedFrameworks: {
        personCentred: false,
        psychodynamic: false,
        cbt: false,
        existential: false,
        gestalt: false
      },
      categories: {
        mentalHealth: [
          'ADHD',
          'Anxiety',
          'Asperger\'s Syndrome',
          'Bipolar Disorder',
          'Body Dysmorphic Disorder',
          'Dementia',
          'Depression',
          'Generalised Anxiety Disorder',
          'Hearing Voices',
          'Hoarding',
          'Obsessive Compulsive Disorder (OCD)',
          'Panic Disorder',
          'Phobias',
          'Postnatal Depression',
          'Psychosis',
          'Schizophrenia',
          'Seasonal Affective Disorder (SAD)'
        ],
        emotionalIssues: [
          'Anger Management',
          'Bullying',
          'Low Self-Confidence',
          'Low Self-Esteem',
          'Passive Aggressive Behaviour',
          'Self-Harm',
          'Stress',
          'Suicidal Thoughts',
          'Work-Related Stress'
        ],
        eatingDisorders: [
          'Anorexia Nervosa',
          'Binge-Eating Disorder',
          'Bulimia Nervosa'
        ],
        trauma: [
          'Bereavement',
          'Domestic Violence',
          'Emotional Abuse',
          'Physical Abuse',
          'Post-Traumatic Stress Disorder (PTSD)',
          'Sexual Abuse'
        ],
        addictions: [
          'Alcoholism',
          'Drug Abuse',
          'Gambling',
          'Internet Addiction',
          'Sex Addiction',
          'Smoking'
        ],
        relationships: [
          'Relationship Issues',
          'Sexual Problems',
          'Sexuality'
        ],
        developmentalCognitive: [
          'Disabilities',
          'Learning Difficulties'
        ],
        lifeChallenges: [
          'Identity Exploration',
          'Pregnancy and Birth',
          'Spirituality'
        ]
      }
    };
  },
  computed: {
    // For single category view - all categories except the active one
    otherCategories() {
      const filteredCategories = {};
      
      for (const categoryName in this.categories) {
        if (categoryName !== this.activeCategory) {
          filteredCategories[categoryName] = this.categories[categoryName];
        }
      }
      
      return filteredCategories;
    },
    
    // For mobile preview - all categories except Mental Health
    otherCategoriesPreview() {
      const preview = {};
      
      for (const categoryName in this.categories) {
        if (categoryName !== 'mentalHealth') {
          preview[categoryName] = this.categories[categoryName];
        }
      }
      
      return preview;
    },
    visibleExperiences() {
      if (this.activeCategory === 'all') {
        // Combine all categories
        let allItems = [];
        for (const category in this.categories) {
          allItems = [...allItems, ...this.categories[category]];
        }
        // Sort alphabetically
        allItems.sort();
        
        // Return first 20 or all if showing more
        return this.showingMore ? allItems : allItems.slice(0, 20);
      } else {
        // Return items from the selected category
        const categoryItems = this.categories[this.activeCategory] || [];
        return this.showingMore ? categoryItems : categoryItems.slice(0, 20);
      }
    },
    
    hasMoreToShow() {
      if (this.activeCategory === 'all') {
        // Check if all items combined is more than 20
        let allItemsCount = 0;
        for (const category in this.categories) {
          allItemsCount += this.categories[category].length;
        }
        return allItemsCount > 20 && !this.showingMore;
      } else {
        // Check if selected category has more than 20 items
        return (this.categories[this.activeCategory]?.length > 20) && !this.showingMore;
      }
    }
  },
  methods: {
    formatCategory(category) {
      // Convert camelCase to Title Case with spaces
      return category
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase());
    },
    formatCategoryName(name) {
      // Convert camelCase to words with spaces
      return name.replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase());
    },
    toggleCategory(category) {
      // Toggle the expanded state for just this category
      this.expandedCategories[category] = !this.expandedCategories[category];
    },
    // Update the showMoreDesktop handler
    toggleShowMoreDesktop() {
      this.showMoreDesktop = !this.showMoreDesktop;
      
      // If switching to "Show Less", reset all expanded categories
      if (!this.showMoreDesktop) {
        for (const category in this.expandedCategories) {
          this.expandedCategories[category] = false;
        }
      }
    },
    // NEW: Toggle framework expansion on mobile
    toggleFramework(framework) {
      this.expandedFrameworks[framework] = !this.expandedFrameworks[framework];
    }
  }
}
</script>

<style scoped>
/* Approach cards */
.approach-card {
  @apply flex items-start p-5 bg-white rounded-lg transition-all duration-300 hover:shadow-md relative overflow-hidden;
  border: 1px solid rgba(210,145,154,0.15);
  transform: translateY(0);
}

.approach-card:hover {
  transform: translateY(-3px);
  border-color: rgba(210,145,154,0.3);
  box-shadow: 0 10px 15px -3px rgba(0,0,0,0.05), 0 4px 6px -2px rgba(0,0,0,0.03);
}

.approach-icon {
  @apply w-12 h-12 rounded-full bg-gradient-to-br from-[#D2919A] to-[#D2919A]/70 flex items-center justify-center mr-4 flex-shrink-0 shadow-sm;
  transition: transform 0.3s ease;
}

.approach-card:hover .approach-icon {
  transform: scale(1.1);
}

.approach-content {
  @apply flex-1;
}

.approach-title {
  @apply text-xl font-semibold text-[#333333] mb-2 relative;
  position: relative;
}

.approach-title::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -5px;
  width: 30%;
  height: 2px;
  background: linear-gradient(to right, rgba(210,145,154,0.7), rgba(210,145,154,0.1));
  transition: width 0.3s ease;
}

.approach-card:hover .approach-title::after {
  width: 50%;
}

/* Framework cards */
.framework-card {
  @apply bg-white rounded-lg transition-all duration-300 relative overflow-hidden;
  border: 1px solid rgba(210,145,154,0.15);
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
}

.framework-card:hover {
  transform: translateY(-3px);
  border-color: rgba(210,145,154,0.3);
  box-shadow: 0 10px 15px -3px rgba(0,0,0,0.05), 0 4px 6px -2px rgba(0,0,0,0.03);
}

.framework-icon {
  @apply w-12 h-12 rounded-full bg-gradient-to-br from-[#D2919A] to-[#D2919A]/70 flex items-center justify-center shadow-sm;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.framework-card:hover .framework-icon {
  transform: scale(1.1);
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
}

.framework-inner {
  @apply p-5 border-l-4 border-[#D2919A]/50;
}

.framework-title {
  @apply text-xl font-semibold text-[#333333] mb-2 relative;
}

/* Category groups */
.category-group {
  @apply p-6 bg-white/90 backdrop-blur-sm rounded-lg transition-all duration-300 h-auto;
  border: 1px solid rgba(210,145,154,0.15);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
}

.category-group:hover {
  box-shadow: 0 10px 15px -3px rgba(0,0,0,0.05), 0 4px 6px -2px rgba(0,0,0,0.03);
  border-color: rgba(210,145,154,0.3);
}

.category-title {
  @apply text-xl font-bold text-[#333333] mb-4 relative inline-block;
  position: relative;
}

.category-title-line {
  @apply absolute -bottom-1 left-0 w-16 h-[2px] bg-gradient-to-r from-[#D2919A] to-[#D2919A]/50;
  transition: width 0.3s ease;
}

.category-group:hover .category-title-line {
  width: 100%;
}

/* Category items */
.category-item {
  @apply py-2.5 px-4 bg-gradient-to-br from-[#F8F4F0] to-[#F5EFE9] rounded-lg transition-all duration-300;
  border: 1px solid rgba(210,145,154,0.15);
  transform: translateY(0);
}

.category-item:hover {
  transform: translateY(-2px);
  border-color: rgba(210,145,154,0.3);
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
}

.category-item-dot {
  @apply h-5 w-5 rounded-full bg-gradient-to-br from-[#D2919A] to-[#D2919A]/70 flex items-center justify-center flex-shrink-0;
}

.category-item-dot::after {
  content: '';
  @apply h-2 w-2 rounded-full bg-white;
}

/* Larger category items for mobile display */
.category-item-large {
  @apply py-3 px-5 bg-gradient-to-br from-[#F8F4F0] to-[#F5EFE9] rounded-lg transition-all duration-300 flex items-center gap-3 shadow-sm;
  border: 1px solid rgba(210,145,154,0.15);
  transform: translateY(0);
}

.category-item-large:hover {
  transform: translateY(-3px);
  border-color: rgba(210,145,154,0.3);
  box-shadow: 0 10px 15px -3px rgba(0,0,0,0.05), 0 4px 6px -2px rgba(0,0,0,0.03);
}

.category-item-large-dot {
  @apply h-6 w-6 rounded-full bg-gradient-to-br from-[#D2919A] to-[#D2919A]/70 flex items-center justify-center flex-shrink-0;
}

.category-item-large-dot::after {
  content: '';
  @apply h-2.5 w-2.5 rounded-full bg-white;
}

/* Smooth transitions for category changes */
.category-group {
  transition: all 0.3s ease-in-out;
}

/* Ensure proper z-index for this section relative to hero video */
.what-i-offer-section {
  position: relative;
  z-index: 1; 
  background-color: #FAF9F7;
}

/* Fix for Safari backdrop-filter */
@supports (backdrop-filter: blur(12px)) or (-webkit-backdrop-filter: blur(12px)) {
  .category-group {
    -webkit-backdrop-filter: blur(12px);
    backdrop-filter: blur(12px);
  }
}

/* Category tab transitions */
.category-tab {
  transition: all 0.3s ease;
}

/* Framework cards with improved hover transitions */
.framework-item {
  transition: all 0.3s ease;
}
.framework-item:hover {
  transform: translateY(-4px);
}

/* Approach items with subtle transitions */
.approach-item {
  transition: all 0.3s ease;
}
.approach-icon-minimal {
  transition: all 0.3s ease;
}
.approach-item:hover .approach-icon-minimal {
  transform: scale(1.1);
}

/* Experience items with hover effects */
.experience-item {
  transition: all 0.25s ease;
}
.experience-item:hover {
  transform: translateY(-2px);
  background-color: rgba(255, 255, 255, 0.8);
}
</style>